import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import { useFiltersContext } from '@openx/components-filters/lib/context/FiltersContext.tsx';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { DirectDealsListFilters } from '../../../types.ts';

type FilterPresetConfig = {
  name: keyof Omit<DirectDealsListFilters, 'phrase' | 'tab'>;
  values: string | string[];
};

export type FilterPresetTabConfig = {
  name: DirectDealsListFilters['tab'];
  label: string;
  isDefault?: boolean;
  filters?: FilterPresetConfig[];
};

type FilterPresetTabsProps = {
  tabs: FilterPresetTabConfig[];
};

export const FilterPresetTabs = ({ tabs }: FilterPresetTabsProps) => {
  const { onFiltersChange, setDisabledFilters, criteria } = useFiltersContext<DirectDealsListFilters>();

  const [searchParams] = useSearchParams();
  const defaultTab = tabs.find(tab => tab.isDefault) || tabs[0];
  const searchParamTab = tabs.find(tab => tab.name === (searchParams.get('tab') as string));
  const initialTab = searchParamTab || defaultTab;

  const [activeTab, setActiveTab] = useState<DirectDealsListFilters['tab']>(initialTab.name);

  const setClearTabFilters = useCallback(
    (newTab: FilterPresetTabConfig) => {
      const tabFilters =
        newTab?.filters?.reduce((acc, filter) => {
          acc[filter.name] = filter.values;
          return acc;
        }, {}) || {};

      onFiltersChange({
        phrase: '',
        tab: newTab.name,
        ...tabFilters,
      });
    },
    [onFiltersChange]
  );

  const setTabDisabledFilters = useCallback(
    (newTab: FilterPresetTabConfig) => {
      const filtersToDisable = newTab?.filters?.map(filter => filter.name) || [];

      setDisabledFilters(['tab', ...filtersToDisable]);
    },
    [setDisabledFilters]
  );

  const sanitizeTabFilters = useCallback(
    (tab: FilterPresetTabConfig) => {
      const existingFilters = criteria?.filters || { phrase: '' };
      const tabFilters =
        tab?.filters?.reduce((acc, filter) => {
          acc[filter.name] = filter.values;
          return acc;
        }, {}) || {};

      onFiltersChange({
        ...existingFilters,
        tab: tab.name,
        ...tabFilters,
      });
    },
    [criteria, onFiltersChange]
  );

  const handleTabChange = useCallback(
    (newTab: FilterPresetTabConfig) => {
      if (newTab.name === activeTab) return;

      setActiveTab(newTab.name);
      setClearTabFilters(newTab);
      setTabDisabledFilters(newTab);
    },
    [activeTab, setClearTabFilters, setTabDisabledFilters]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: set tab with corresponding criteria on page mount
  useEffect(() => {
    setActiveTab(initialTab.name);
    sanitizeTabFilters(initialTab);
    setTabDisabledFilters(initialTab);
  }, []);

  return (
    <Tabs value={activeTab} aria-label="tabs" sx={{ borderBottom: 0, backgroundColor: 'background.boxDark' }}>
      {tabs.map(tab => (
        <Tab
          data-test={`tab-${tab.label.toLowerCase()}`}
          key={tab.name}
          value={tab.name}
          label={tab.label}
          onClick={() => {
            handleTabChange(tab);
          }}
          sx={{
            textTransform: 'none',
            borderBottom: activeTab === tab.name ? `2px solid ${palette.primary.main}` : 'none',
          }}
        />
      ))}
    </Tabs>
  );
};
